import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/IndustriesOffer.css';
import tickIcon from '../assets/industries-Images/tickIcon.svg';

interface IndustriesOfferProps<T> {
  items: T[];
  idKey: keyof T;
  imagePath: (item: T) => string;
  titlePath: (item: T) => string;
  descriptionPath: (item: T) => string;
  highlightsPath: (item: T) => string[];
  linkPath: (item: T) => string;
}

const IndustriesOffer = <T,>({
  items,
  idKey,
  imagePath,
  titlePath,
  descriptionPath,
  highlightsPath,
  linkPath
}: IndustriesOfferProps<T>) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const lastScrollY = useRef(0);
  const scrollingDown = useRef(true);

  // Initialize card refs array
  useEffect(() => {
    cardRefs.current = items.map((_, i) => cardRefs.current[i] ?? null);
  }, [items]);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const currentScrollY = window.scrollY;
      scrollingDown.current = currentScrollY > lastScrollY.current;
      lastScrollY.current = currentScrollY;

      // Define a proper type for card positions
      type CardPosition = {
        index: number;
        top: number;
        bottom: number;
        center: number;
      };

      // Get positions of all cards
      const cardPositions: CardPosition[] = cardRefs.current
        .filter((card): card is HTMLDivElement => card !== null)
        .map((card, index) => {
          const rect = card.getBoundingClientRect();
          return {
            index,
            top: rect.top,
            bottom: rect.bottom,
            center: rect.top + rect.height / 2
          };
        });

      if (cardPositions.length === 0) return;

      let newActiveIndex = activeIndex;

      if (scrollingDown.current) {
        // When scrolling down, find the first card whose center is past the middle of the viewport
        for (let i = activeIndex; i < cardPositions.length; i++) {
          if (cardPositions[i].center <= window.innerHeight * 0.6) {
            newActiveIndex = i;
          } else {
            break;
          }
        }
      } else {
        // When scrolling up, find the last card whose center is above the middle of the viewport
        for (let i = activeIndex; i >= 0; i--) {
          if (cardPositions[i].center >= window.innerHeight * 0.4) {
            newActiveIndex = i;
          } else {
            break;
          }
        }
      }

      setActiveIndex(newActiveIndex);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeIndex]);

  // Calculate the position for the circle and dot
  const calculateMarkerPosition = () => {
    if (cardRefs.current.length === 0 || !containerRef.current) return 120;

    const activeCard = cardRefs.current[activeIndex];
    if (!activeCard) return 120;

    const containerRect = containerRef.current.getBoundingClientRect();
    const cardRect = activeCard.getBoundingClientRect();

    // Calculate position relative to container
    const position = cardRect.top - containerRect.top + (cardRect.height / 2);
    return Math.max(120, position); // Minimum position is 120px (initial position)
  };

  const markerPosition = calculateMarkerPosition();

  return (
    <div className="industries-grid" ref={containerRef}>
      {/* Static full-height vertical gradient line */}
      <div className="industries-line"></div>

      {/* Animated big circle */}
      <div
        className="industries-circle"
        style={{
          top: `${markerPosition}px`,
        }}
      ></div>

      {/* Animated dot */}
      <div
        className="industries-dot"
        style={{
          top: `${markerPosition}px`,
        }}
      ></div>

      {items.map((item, index) => (
        <div
          key={String(item[idKey])}
          className={`industry-row ${index % 2 === 0 ? 'even' : 'odd'}`}
          ref={el => {
            if (el) {
              cardRefs.current[index] = el;
            }
          }}
        >
          {/* Rest of your card content remains the same */}
          <div className="industry-image-container">
            <div className="image-number-container">
              <span className="image-number">{String(index + 1).padStart(2, '0')}</span>
            </div>
            <div className='industry-image-container-image'>
              <img
                src={imagePath(item)}
                alt={titlePath(item)}
                className="industry-image"
              />
            </div>
          </div>

          <Link to={linkPath(item)} className="industry-card-link">
            <div className="industry-card">
              <div className="industry-card-header">
                <h3 className="industry-title">{titlePath(item)}</h3>
              </div>
              <div className="industry-content">
                <p className="industry-description">
                  {descriptionPath(item)}
                </p>
                <div className="industry-features">
                  {highlightsPath(item)?.slice(0, 3).map((feature, i) => (
                    <div key={i} className="industry-feature">
                      <div className="industry-feature-icon">
                        <img src={tickIcon} alt="" />
                      </div>
                      <div className="industry-feature-text">{feature}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Link>
        </div>
      ))}
    </div>
  );
};

export default IndustriesOffer;
